'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { Award01IconSolid, Rocket01IconSolid } from '@/components/ui/icon/glyphs';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/ui/utils';
import type { LifecycleModalPayload, LifecycleVariant } from '@/stores/market-lifecycle-modal';
export interface LifecycleModalViewProps {
  payload: LifecycleModalPayload;
  /** Dismiss the modal before the selected CTA continues. */
  onClose: () => void;
}
type Tone = 'positive' | 'negative' | 'brand';
type PrimaryCta = 'trade' | 'claim' | 'browse';
interface VariantPresentation {
  tone: Tone;
  heroIcon: IconSvgElement;
  /** Show the winning-outcome label chip (resolution variants only). */
  showWinnerChip: boolean;
  primary: PrimaryCta;
  /** Show the secondary "Claim backer rewards" CTA. */
  showBackerCta: boolean;
}
const TONE_CLASSES: Record<Tone, {
  glow: string;
  iconWrap: string;
  icon: string;
}> = {
  positive: {
    glow: 'lifecycle-glow-positive',
    iconWrap: 'bg-feedback-success-surface',
    icon: 'text-feedback-success-content'
  },
  negative: {
    glow: 'lifecycle-glow-negative',
    iconWrap: 'bg-feedback-error-surface',
    icon: 'text-feedback-error-content'
  },
  brand: {
    glow: 'lifecycle-glow-brand',
    iconWrap: 'bg-background-brand',
    icon: 'text-foreground-brand'
  }
};
function toneForVariant(variant: LifecycleVariant): Tone {
  switch (variant) {
    case 'resolution-won-nonbacker':
    case 'resolution-won-backer':
      return 'positive';
    case 'resolution-lost-nonbacker':
    case 'resolution-lost-backer':
      return 'negative';
    default:
      return 'brand';
  }
}
function presentationFor(variant: LifecycleVariant, isBacker: boolean): VariantPresentation {
  const tone = toneForVariant(variant);
  switch (variant) {
    case 'graduation-generic':
    case 'graduation-backer':
    case 'graduation-creator':
      return {
        tone,
        heroIcon: Rocket01IconSolid,
        showWinnerChip: false,
        primary: 'trade',
        showBackerCta: false
      };
    case 'resolution-won-nonbacker':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'claim',
        showBackerCta: false
      };
    case 'resolution-won-backer':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'claim',
        showBackerCta: true
      };
    case 'resolution-lost-nonbacker':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'browse',
        showBackerCta: false
      };
    case 'resolution-lost-backer':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'browse',
        showBackerCta: true
      };
    case 'resolution-not-holder':
      // A pure backer with no outcome holdings still gets the backer claim path.
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'browse',
        showBackerCta: isBacker
      };
  }
}

/**
 * Presentational renderer for the lifecycle-modal variants. Branches on
 * `payload.variant` to pick a tone (corner glow), hero icon, copy, and the
 * primary / secondary CTAs. No animation is added here, so reduced-motion is
 * satisfied by construction (the only motion is SilkSheet's own overlay
 * transition, which already honors `prefers-reduced-motion`).
 *
 * NOTE (spec 112, FR-018): the hero is currently an icon-based placeholder.
 * When the final Figma art lands, swap the hero for the winning-outcome image
 * — render it via `OptimizedImage`/`OutcomeIndicator` so the NSFW overlay is
 * threaded (the `winningOutcome*` payload fields are carried for that purpose).
 */
export function LifecycleModalView(t0) {
  const $ = _c(110);
  const {
    payload,
    onClose
  } = t0;
  const t = useTranslations("market.lifecycle_modal");
  let t1;
  if ($[0] !== payload.isBacker || $[1] !== payload.variant) {
    t1 = presentationFor(payload.variant, payload.isBacker);
    $[0] = payload.isBacker;
    $[1] = payload.variant;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const p = t1;
  const tone = TONE_CLASSES[p.tone];
  const label = payload.winningOutcomeLabel ?? "";
  const t2 = payload.winnerOutcomeIndex ?? 0;
  let t3;
  if ($[3] !== label || $[4] !== t2) {
    t3 = {
      title: label,
      index: t2
    };
    $[3] = label;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const winnerColor = useOutcomeColor(t3);
  let t4;
  bb0: {
    switch (payload.variant) {
      case "graduation-generic":
        {
          let t5;
          if ($[6] !== t) {
            t5 = t("graduationGeneric.headline");
            $[6] = t;
            $[7] = t5;
          } else {
            t5 = $[7];
          }
          let t6;
          if ($[8] !== t) {
            t6 = t("graduationGeneric.body");
            $[8] = t;
            $[9] = t6;
          } else {
            t6 = $[9];
          }
          let t7;
          if ($[10] !== t5 || $[11] !== t6) {
            t7 = {
              headline: t5,
              body: t6
            };
            $[10] = t5;
            $[11] = t6;
            $[12] = t7;
          } else {
            t7 = $[12];
          }
          t4 = t7;
          break bb0;
        }
      case "graduation-creator":
        {
          let t5;
          if ($[13] !== t) {
            t5 = t("graduationCreator.headline");
            $[13] = t;
            $[14] = t5;
          } else {
            t5 = $[14];
          }
          let t6;
          if ($[15] !== t) {
            t6 = t("graduationCreator.body");
            $[15] = t;
            $[16] = t6;
          } else {
            t6 = $[16];
          }
          let t7;
          if ($[17] !== t5 || $[18] !== t6) {
            t7 = {
              headline: t5,
              body: t6
            };
            $[17] = t5;
            $[18] = t6;
            $[19] = t7;
          } else {
            t7 = $[19];
          }
          t4 = t7;
          break bb0;
        }
      case "graduation-backer":
        {
          let t5;
          if ($[20] !== t) {
            t5 = t("graduationBacker.headline");
            $[20] = t;
            $[21] = t5;
          } else {
            t5 = $[21];
          }
          let t6;
          if ($[22] !== t) {
            t6 = t("graduationBacker.body");
            $[22] = t;
            $[23] = t6;
          } else {
            t6 = $[23];
          }
          let t7;
          if ($[24] !== t5 || $[25] !== t6) {
            t7 = {
              headline: t5,
              body: t6
            };
            $[24] = t5;
            $[25] = t6;
            $[26] = t7;
          } else {
            t7 = $[26];
          }
          t4 = t7;
          break bb0;
        }
      case "resolution-won-nonbacker":
        {
          let t5;
          if ($[27] !== t) {
            t5 = t("wonNonbacker.headline");
            $[27] = t;
            $[28] = t5;
          } else {
            t5 = $[28];
          }
          let t6;
          if ($[29] !== label || $[30] !== t) {
            t6 = t("wonNonbacker.body", {
              label
            });
            $[29] = label;
            $[30] = t;
            $[31] = t6;
          } else {
            t6 = $[31];
          }
          let t7;
          if ($[32] !== t5 || $[33] !== t6) {
            t7 = {
              headline: t5,
              body: t6
            };
            $[32] = t5;
            $[33] = t6;
            $[34] = t7;
          } else {
            t7 = $[34];
          }
          t4 = t7;
          break bb0;
        }
      case "resolution-won-backer":
        {
          let t5;
          if ($[35] !== t) {
            t5 = t("wonBacker.headline");
            $[35] = t;
            $[36] = t5;
          } else {
            t5 = $[36];
          }
          let t6;
          if ($[37] !== label || $[38] !== t) {
            t6 = t("wonBacker.body", {
              label
            });
            $[37] = label;
            $[38] = t;
            $[39] = t6;
          } else {
            t6 = $[39];
          }
          let t7;
          if ($[40] !== t5 || $[41] !== t6) {
            t7 = {
              headline: t5,
              body: t6
            };
            $[40] = t5;
            $[41] = t6;
            $[42] = t7;
          } else {
            t7 = $[42];
          }
          t4 = t7;
          break bb0;
        }
      case "resolution-lost-nonbacker":
        {
          let t5;
          if ($[43] !== t) {
            t5 = t("lostNonbacker.headline");
            $[43] = t;
            $[44] = t5;
          } else {
            t5 = $[44];
          }
          let t6;
          if ($[45] !== label || $[46] !== t) {
            t6 = t("lostNonbacker.body", {
              label
            });
            $[45] = label;
            $[46] = t;
            $[47] = t6;
          } else {
            t6 = $[47];
          }
          let t7;
          if ($[48] !== t5 || $[49] !== t6) {
            t7 = {
              headline: t5,
              body: t6
            };
            $[48] = t5;
            $[49] = t6;
            $[50] = t7;
          } else {
            t7 = $[50];
          }
          t4 = t7;
          break bb0;
        }
      case "resolution-lost-backer":
        {
          let t5;
          if ($[51] !== t) {
            t5 = t("lostBacker.headline");
            $[51] = t;
            $[52] = t5;
          } else {
            t5 = $[52];
          }
          let t6;
          if ($[53] !== label || $[54] !== t) {
            t6 = t("lostBacker.body", {
              label
            });
            $[53] = label;
            $[54] = t;
            $[55] = t6;
          } else {
            t6 = $[55];
          }
          let t7;
          if ($[56] !== t5 || $[57] !== t6) {
            t7 = {
              headline: t5,
              body: t6
            };
            $[56] = t5;
            $[57] = t6;
            $[58] = t7;
          } else {
            t7 = $[58];
          }
          t4 = t7;
          break bb0;
        }
      case "resolution-not-holder":
        {
          let t5;
          if ($[59] !== t) {
            t5 = t("notHolder.headline");
            $[59] = t;
            $[60] = t5;
          } else {
            t5 = $[60];
          }
          let t6;
          if ($[61] !== label || $[62] !== t) {
            t6 = t("notHolder.body", {
              label
            });
            $[61] = label;
            $[62] = t;
            $[63] = t6;
          } else {
            t6 = $[63];
          }
          let t7;
          if ($[64] !== t5 || $[65] !== t6) {
            t7 = {
              headline: t5,
              body: t6
            };
            $[64] = t5;
            $[65] = t6;
            $[66] = t7;
          } else {
            t7 = $[66];
          }
          t4 = t7;
          break bb0;
        }
    }
    t4 = undefined;
  }
  const copy = t4;
  const claim = payload.claim;
  const claimClaimed = claim?.claimed === true;
  const claimReady = claim?.finalized === true && claim?.claimable === true && !claimClaimed;
  let t5;
  if ($[67] !== claimClaimed || $[68] !== claimReady || $[69] !== t) {
    t5 = claimClaimed ? t("cta.claimed") : claimReady ? t("cta.claim") : t("cta.claimPending");
    $[67] = claimClaimed;
    $[68] = claimReady;
    $[69] = t;
    $[70] = t5;
  } else {
    t5 = $[70];
  }
  const claimLabel = t5;
  let t6;
  if ($[71] !== tone.iconWrap) {
    t6 = cn("flex size-16 items-center justify-center rounded-full", tone.iconWrap);
    $[71] = tone.iconWrap;
    $[72] = t6;
  } else {
    t6 = $[72];
  }
  const t7 = p.heroIcon;
  let t8;
  if ($[73] !== tone.icon) {
    t8 = cn("size-8", tone.icon);
    $[73] = tone.icon;
    $[74] = t8;
  } else {
    t8 = $[74];
  }
  let t9;
  if ($[75] !== p.heroIcon || $[76] !== t8) {
    t9 = <Icon icon={t7} className={t8} aria-hidden="true" />;
    $[75] = p.heroIcon;
    $[76] = t8;
    $[77] = t9;
  } else {
    t9 = $[77];
  }
  let t10;
  if ($[78] !== t6 || $[79] !== t9) {
    t10 = <div className={t6}>{t9}</div>;
    $[78] = t6;
    $[79] = t9;
    $[80] = t10;
  } else {
    t10 = $[80];
  }
  let t11;
  if ($[81] !== copy.headline) {
    t11 = <h2 className="text-heading font-bold text-foreground-primary">{copy.headline}</h2>;
    $[81] = copy.headline;
    $[82] = t11;
  } else {
    t11 = $[82];
  }
  let t12;
  if ($[83] !== copy.body) {
    t12 = <p className="text-body text-foreground-secondary">{copy.body}</p>;
    $[83] = copy.body;
    $[84] = t12;
  } else {
    t12 = $[84];
  }
  let t13;
  if ($[85] !== t11 || $[86] !== t12) {
    t13 = <div className="flex flex-col items-center gap-2">{t11}{t12}</div>;
    $[85] = t11;
    $[86] = t12;
    $[87] = t13;
  } else {
    t13 = $[87];
  }
  let t14;
  if ($[88] !== p.showWinnerChip || $[89] !== payload.winningOutcomeLabel || $[90] !== winnerColor) {
    t14 = p.showWinnerChip && payload.winningOutcomeLabel !== null ? <span className={cn("inline-flex w-fit items-center gap-1.5 rounded-pill px-2.5 py-1", winnerColor.outline)}><Icon icon={Award01IconSolid} className={cn("size-3.5", winnerColor.text)} aria-hidden="true" /><span className={cn("text-caption font-semibold", winnerColor.text)}>{payload.winningOutcomeLabel}</span></span> : null;
    $[88] = p.showWinnerChip;
    $[89] = payload.winningOutcomeLabel;
    $[90] = winnerColor;
    $[91] = t14;
  } else {
    t14 = $[91];
  }
  let t15;
  if ($[92] !== claimLabel || $[93] !== claimReady || $[94] !== onClose || $[95] !== p.primary || $[96] !== t) {
    t15 = p.primary === "browse" ? <AnimatedButton asChild={true} variant="primary" size="lg" className="w-full"><Link href="/markets" onClick={onClose}>{t("cta.browse")}</Link></AnimatedButton> : p.primary === "trade" ? <AnimatedButton variant="primary" size="lg" className="w-full" onClick={onClose}>{t("cta.trade")}</AnimatedButton> : <AnimatedButton variant="primary" size="lg" className="w-full" onClick={onClose} disabled={!claimReady}>{claimLabel}</AnimatedButton>;
    $[92] = claimLabel;
    $[93] = claimReady;
    $[94] = onClose;
    $[95] = p.primary;
    $[96] = t;
    $[97] = t15;
  } else {
    t15 = $[97];
  }
  let t16;
  if ($[98] !== onClose || $[99] !== p.showBackerCta || $[100] !== t) {
    t16 = p.showBackerCta ? <AnimatedButton variant="secondary" size="lg" className="w-full" onClick={onClose}>{t("cta.claimBackerRewards")}</AnimatedButton> : null;
    $[98] = onClose;
    $[99] = p.showBackerCta;
    $[100] = t;
    $[101] = t16;
  } else {
    t16 = $[101];
  }
  let t17;
  if ($[102] !== t15 || $[103] !== t16) {
    t17 = <div className="flex w-full flex-col gap-2 pt-2">{t15}{t16}</div>;
    $[102] = t15;
    $[103] = t16;
    $[104] = t17;
  } else {
    t17 = $[104];
  }
  let t18;
  if ($[105] !== t10 || $[106] !== t13 || $[107] !== t14 || $[108] !== t17) {
    t18 = <div className="relative flex flex-col items-center gap-4 text-center">{t10}{t13}{t14}{t17}</div>;
    $[105] = t10;
    $[106] = t13;
    $[107] = t14;
    $[108] = t17;
    $[109] = t18;
  } else {
    t18 = $[109];
  }
  return t18;
}